/**
 * Records a single raw audio clip for the phoneme evaluator (see
 * {@link import('./phoneme')}).
 *
 * The on-device speech path (`lib/speech.ts`) uses the platform recognizer,
 * which never exposes the captured audio — it only returns a transcript. The
 * phoneme API needs the actual bytes, so this is a separate capture path that
 * yields a `Blob`.
 *
 * There are two capture engines behind one API:
 *
 *  - **Continuous** (preferred). The caller opens the microphone once for the
 *    whole exercise with {@link openMic}; from then on every frame of audio
 *    lands in a rolling in-memory buffer (`lib/audio-ring.ts`). A capture is
 *    then just a pair of marks on that buffer, which lets us cut the clip
 *    {@link PRE_ROLL_MS} *before* the start tap and {@link POST_ROLL_MS}
 *    *after* the stop tap. That is the whole point: both taps are late
 *    relative to the speaker — the mic isn't live yet when the word begins and
 *    the last phoneme is still trailing when stop is hit — and the scorer read
 *    those truncations as deletions the patient never made. Output is WAV.
 *  - **One-shot** (fallback). The classic `getUserMedia` + `MediaRecorder`:
 *    open the mic on the start tap, close it on the stop tap. Used when
 *    {@link openMic} was never called or couldn't get a Web Audio graph up.
 *    No pre/post-roll is possible here — there is no audio from before the
 *    tap to cut from. Output is whatever container the platform produces.
 *
 * Both run unchanged in a browser AND inside the Capacitor iOS/Android WebView
 * — WKWebView has supported `getUserMedia`/`MediaRecorder`/Web Audio since iOS
 * 14.3 (our deployment target is 15), and Android's Chromium WebView likewise.
 * So we do NOT need a native recorder plugin (and the pure-SPM iOS project
 * can't consume the CocoaPods-only ones anyway). The OS mic prompt is raised
 * by the WebView on the first `getUserMedia`, gated by
 * `NSMicrophoneUsageDescription` (iOS) / `RECORD_AUDIO` (Android), both
 * already declared.
 *
 * Two callers reach this: the therapist (web) phoneme path — the phoneme engine
 * requires `isWeb` (see `lib/speech-engine.ts`) — and the self-assessment drill
 * in `TrainingView`, which records the patient so they can hear themselves
 * before judging. The latter runs on the patient (Capacitor) build too.
 *
 * Start/stop is explicit: the caller taps to start and taps again to stop, then
 * we hand back the clip. {@link abortRecording} unwinds an in-flight capture on
 * pause / close without producing a clip. As an optional convenience the caller
 * may pass `vad` hooks to {@link startRecording} to also auto-stop on silence
 * (local voice-activity detection, see `lib/vad.ts`) — strictly opt-in per
 * call; the manual tap always remains available as an override.
 */

import {
  createRingBuffer,
  encodeWav,
  msToSamples,
  rmsOfFloat32,
  samplesToMs,
  type RingBuffer,
} from './audio-ring'
import {
  attachVad,
  createSpeechEndpointer,
  isTerminalVadState,
  vadEnabled,
  type SpeechEndpointer,
  type VadHooks,
} from './vad'

export interface RecordedClip {
  blob: Blob
  /** File extension for the multipart filename. */
  ext: 'webm' | 'ogg' | 'm4a' | 'mp4' | 'wav'
  /** MIME type of the blob. */
  mime: string
}

/** How much audio from before the start tap is prepended to the clip. */
export const PRE_ROLL_MS = 500
/** How much audio from after the stop tap is appended to the clip. */
export const POST_ROLL_MS = 500

/**
 * Seconds of audio kept in the rolling buffer. Comfortably longer than any
 * single utterance (the VAD's own safety ceiling is 8 s) with room for a
 * therapist who holds the button; a longer capture simply keeps its last 30 s.
 * At 16 kHz mono float that is ~1.9 MB resident, and ~1 MB once encoded.
 */
const RING_SECONDS = 30

/**
 * Requested capture rate. 16 kHz is what speech models want anyway, and asking
 * the `AudioContext` for it means the browser resamples properly instead of us
 * decimating and aliasing. Browsers that reject the rate keep their native one
 * — everything downstream reads `ctx.sampleRate`, so that path is just larger.
 */
const TARGET_SAMPLE_RATE = 16000

/**
 * `ScriptProcessorNode` frame size. 1024 samples is ~64 ms at 16 kHz — fine
 * for both the VAD (which polled at 50 ms before) and for post-roll precision,
 * while staying large enough not to stutter on the main thread.
 */
const FRAME_SIZE = 1024

const DEV = import.meta.env.DEV
function devLog(message: string, ...args: unknown[]): void {
  if (DEV) console.log(`[audio-recorder] ${message}`, ...args)
}

type AudioContextCtor = typeof AudioContext
function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as typeof window & { webkitAudioContext?: AudioContextCtor }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

/**
 * Whether the running WebView/browser can capture audio at all. Used to give
 * a clean banner instead of an opaque throw on the rare platform without
 * either capture engine.
 */
export function isRecordingSupported(): boolean {
  if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia === undefined) {
    return false
  }
  return typeof MediaRecorder !== 'undefined' || getAudioContextCtor() !== null
}

/**
 * Ensure microphone-recording permission. We return true optimistically and
 * let `getUserMedia` raise the OS/WebView prompt — a denial then surfaces
 * there as a rejection the caller maps to a banner. (A pre-flight
 * `permissions.query({name:'microphone'})` isn't reliable in the iOS WebView,
 * so we don't gate on it.)
 */
export async function ensureMicPermission(): Promise<boolean> {
  return isRecordingSupported()
}

// --- continuous capture state ---------------------------------------------

interface ContinuousCapture {
  stream: MediaStream
  ctx: AudioContext
  source: MediaStreamAudioSourceNode
  processor: ScriptProcessorNode
  sink: GainNode
  ring: RingBuffer
  sampleRate: number
}

/** One in-flight cut of the rolling buffer, in absolute sample indices. */
interface Utterance {
  /** Start tap minus the pre-roll (the ring clamps if it reaches back too far). */
  startSample: number
  /** Set the moment the turn ends — by stop tap or by VAD. `null` while live. */
  endSample: number | null
  /** True when VAD, not the user, ended it: the trailing silence is already in. */
  endedByVad: boolean
  endpointer: SpeechEndpointer | null
  hooks: VadHooks | null
}

let continuous: ContinuousCapture | null = null
let utterance: Utterance | null = null
/** Resolver for a {@link stopRecording} waiting on its post-roll to arrive. */
let pendingTail: { target: number; resolve: () => void } | null = null

// --- one-shot (MediaRecorder) capture state -------------------------------

let mediaRecorder: MediaRecorder | null = null
let chunks: Blob[] = []
let activeStream: MediaStream | null = null
/** Teardown for the optional VAD graph attached to the one-shot capture. */
let vadTeardown: (() => void) | null = null

function cleanupOneShot(): void {
  vadTeardown?.()
  vadTeardown = null
  activeStream?.getTracks().forEach((t) => t.stop())
  activeStream = null
  mediaRecorder = null
  chunks = []
}

// --- continuous capture ---------------------------------------------------

/**
 * Whether the caller's `vad` hooks should actually drive an auto-stop. Passing
 * `vad` is already an explicit per-call opt-in, so the only remaining gate is
 * the kill switch (`VITE_PHONEME_VAD`).
 */
function vadWanted(opts: StartRecordingOptions): boolean {
  return opts.vad !== undefined && vadEnabled
}

/** Release a waiting {@link stopRecording} without making it wait for a timer. */
function resolveTail(): void {
  const tail = pendingTail
  pendingTail = null
  tail?.resolve()
}

/**
 * Feed one frame to the live utterance: VAD while it's still open, and the
 * post-roll check for a `stopRecording` that is waiting on the tail.
 */
function onFrame(frame: Float32Array, ring: RingBuffer, sampleRate: number): void {
  const u = utterance
  if (u !== null && u.endSample === null && u.endpointer !== null) {
    const state = u.endpointer.push(rmsOfFloat32(frame), samplesToMs(ring.written(), sampleRate))
    if (isTerminalVadState(state)) {
      // Latch the end here rather than in the hook: the trailing silence the
      // endpointer just measured is exactly the tail we want, so the clip ends
      // now and needs no post-roll of its own.
      u.endSample = ring.written()
      u.endedByVad = true
      const hooks = u.hooks
      u.endpointer = null
      u.hooks = null
      devLog(`vad verdict=${state}`)
      if (state === 'timeout-nospeech') (hooks?.onNoSpeech ?? hooks?.onEnd)?.()
      else hooks?.onEnd?.()
    }
  }
  if (pendingTail !== null && ring.written() >= pendingTail.target) resolveTail()
}

/**
 * Open the microphone for the whole exercise and start filling the rolling
 * buffer. Idempotent, and never throws — a failure (permission denied, no Web
 * Audio, no device) resolves `false` and simply leaves {@link startRecording}
 * on the one-shot engine, which surfaces the real error on the tap where the
 * caller already handles it.
 *
 * Callers should pair this with {@link closeMic} on pause / unmount: the
 * browser shows a recording indicator for as long as the stream is live, and
 * holding it through a paused exercise would be both rude and confusing.
 */
/**
 * The `speaker` argument exists only so this matches the docked
 * implementation's signature - `drill-recorder` picks one at load and callers
 * cannot tell which they got. Opening a microphone on this device records
 * whoever is at it; there is no second track to choose from.
 */
export async function openMic(_speaker?: 'peer' | 'self'): Promise<boolean> {
  if (continuous !== null) {
    // Idempotent — and it doubles as "make sure it's actually running". A
    // context built outside a user gesture starts suspended (autoplay policy),
    // in which case no frames reach the ring; calling this again from the mic
    // tap, which IS a gesture, is what gets the buffer filling.
    if (continuous.ctx.state === 'suspended') await continuous.ctx.resume().catch(() => {})
    return true
  }
  const Ctx = getAudioContextCtor()
  if (Ctx === null || navigator.mediaDevices?.getUserMedia === undefined) return false

  let stream: MediaStream | null = null
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    let ctx: AudioContext
    try {
      ctx = new Ctx({ sampleRate: TARGET_SAMPLE_RATE })
    } catch {
      // Safari has historically rejected an explicit rate; native is fine.
      ctx = new Ctx()
    }
    // A context can start suspended; `openMic` runs inside a user gesture, so
    // resume is allowed here. Ignore rejection — the graph still runs.
    void ctx.resume().catch(() => {})

    const sampleRate = ctx.sampleRate
    const ring = createRingBuffer(Math.ceil(RING_SECONDS * sampleRate))
    const source = ctx.createMediaStreamSource(stream)
    // One input channel downmixes a stereo mic to the mono the scorer wants.
    const processor = ctx.createScriptProcessor(FRAME_SIZE, 1, 1)
    // Chrome only pumps a ScriptProcessorNode that reaches the destination, so
    // route it through a muted gain — audible monitoring would be an echo.
    const sink = ctx.createGain()
    sink.gain.value = 0

    processor.onaudioprocess = (e) => {
      // The event's buffer is reused between callbacks; `write` copies out of
      // it immediately, so no retained reference escapes this handler.
      const input = e.inputBuffer.getChannelData(0)
      ring.write(input)
      onFrame(input, ring, sampleRate)
    }

    source.connect(processor)
    processor.connect(sink)
    sink.connect(ctx.destination)

    continuous = { stream, ctx, source, processor, sink, ring, sampleRate }
    devLog(`openMic sampleRate=${sampleRate} ring=${RING_SECONDS}s`)
    return true
  } catch (e) {
    stream?.getTracks().forEach((t) => t.stop())
    devLog('openMic failed', e)
    return false
  }
}

/**
 * Release the continuously-open microphone and drop the rolling buffer. Also
 * discards any in-flight utterance. Idempotent and never throws.
 */
export function closeMic(): void {
  const c = continuous
  continuous = null
  utterance = null
  resolveTail()
  if (c === null) return
  c.processor.onaudioprocess = null
  for (const node of [c.source, c.processor, c.sink]) {
    try {
      node.disconnect()
    } catch {
      /* already gone */
    }
  }
  c.stream.getTracks().forEach((t) => t.stop())
  // close() returns a promise; nothing downstream depends on it.
  void c.ctx.close().catch(() => {})
  devLog('closeMic')
}

/** Whether the rolling buffer is live — i.e. whether pre-roll is available. */
export function isMicOpen(): boolean {
  return continuous !== null
}

/**
 * Wait until the ring has `target` samples, i.e. until the post-roll has been
 * captured. Resolves early if the graph is gone, and is backstopped by a timer
 * so a stalled audio thread can never hang the scoring flow.
 */
function waitForTail(target: number, timeoutMs: number): Promise<void> {
  const c = continuous
  if (c === null || c.ring.written() >= target) return Promise.resolve()
  return new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      pendingTail = null
      resolve()
    }, timeoutMs)
    pendingTail = {
      target,
      resolve: () => {
        clearTimeout(timer)
        resolve()
      },
    }
  })
}

// --- public API -----------------------------------------------------------

/** Options for {@link startRecording}. */
export interface StartRecordingOptions {
  /**
   * How much audio from before the start of the utterance to keep, overriding
   * {@link PRE_ROLL_MS}. Only the docked recorder honours it, where the start
   * signal arrives over a network and the look-back has to cover that trip.
   */
  preRollMs?: number
  /**
   * Auto-stop hooks. When supplied (and not disabled by the `vadEnabled` kill
   * switch), local voice-activity detection watches the mic and fires these on
   * silence / never-spoke. The recorder does NOT call {@link stopRecording}
   * itself — the hook owner does. Omitting `vad` leaves the capture purely
   * tap-to-stop.
   */
  vad?: VadHooks
}

/** Options for {@link stopRecording}. */
export interface StopRecordingOptions {
  /**
   * Override the trailing audio kept past the stop tap. Defaults to
   * {@link POST_ROLL_MS}, or to 0 when VAD ended the turn (the endpointer's
   * own silence window already IS the tail). Ignored by the one-shot engine,
   * which has no buffered future to draw on.
   */
  postRollMs?: number
}

/**
 * Begin capturing. Resolves once the engine is running; rejects if the mic
 * can't be opened — permission denial, no device, or insecure context. The
 * rejection's `DOMException.name` (`NotAllowedError`, `NotFoundError`, …) lets
 * the caller pick the right banner.
 *
 * With {@link openMic} already in effect this only marks the rolling buffer
 * and cannot fail on permissions — the mic is already ours.
 */
export async function startRecording(opts: StartRecordingOptions = {}): Promise<void> {
  const c = continuous
  if (c !== null) {
    utterance = {
      startSample: Math.max(0, c.ring.written() - msToSamples(PRE_ROLL_MS, c.sampleRate)),
      endSample: null,
      endedByVad: false,
      // The endpointer starts at the tap, not at the pre-roll, so its
      // calibration window still measures the room the way it always has.
      endpointer: vadWanted(opts) ? createSpeechEndpointer() : null,
      hooks: opts.vad ?? null,
    }
    devLog(`startRecording (continuous) from sample ${utterance.startSample}`)
    return
  }

  if (!isRecordingSupported() || typeof MediaRecorder === 'undefined') {
    throw new DOMException('MediaRecorder unavailable', 'NotSupportedError')
  }
  devLog('startRecording (one-shot): getUserMedia + MediaRecorder')
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  activeStream = stream
  chunks = []
  const mr = new MediaRecorder(stream)
  mr.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data)
  }
  mediaRecorder = mr
  mr.start()

  // Auto-stop is strictly opt-in. `attachVad` is itself a no-op when the Web
  // Audio API is missing, so this degrades to manual stop.
  if (opts.vad !== undefined && vadWanted(opts)) {
    vadTeardown = attachVad(stream, opts.vad)
  }
}

/**
 * Stop capturing and resolve with the recorded clip. Rejects if no capture is
 * in progress or the engine fails to finalise.
 *
 * On the continuous engine this waits out the post-roll and then cuts
 * `[tap - PRE_ROLL_MS, tap + POST_ROLL_MS)` out of the rolling buffer as a
 * WAV. On the one-shot engine the container is whatever the platform's
 * MediaRecorder produces — webm/ogg on Chromium, mp4/AAC in WebKit. The
 * server's ffmpeg decodes all of them.
 */
export async function stopRecording(opts: StopRecordingOptions = {}): Promise<RecordedClip> {
  const c = continuous
  if (c !== null) {
    const u = utterance
    if (u === null) throw new Error('not recording')
    if (u.endSample === null) u.endSample = c.ring.written()
    u.endpointer = null
    u.hooks = null

    const postRollMs = opts.postRollMs ?? (u.endedByVad ? 0 : POST_ROLL_MS)
    const target = u.endSample + msToSamples(postRollMs, c.sampleRate)
    // Generous backstop: we only ever wait ~half a second of real audio, so a
    // timeout here means the audio thread died, not that the tail is slow.
    if (postRollMs > 0) await waitForTail(target, postRollMs + 1000)

    // `continuous` may have been closed (pause / unmount) while we waited.
    const live = continuous
    if (live === null || utterance !== u) throw new Error('capture interrupted')
    const samples = live.ring.read(u.startSample, target)
    utterance = null
    const blob = encodeWav(samples, live.sampleRate)
    devLog(
      `stopRecording (continuous) ${samplesToMs(samples.length, live.sampleRate).toFixed(0)}ms ` +
        `pre=${PRE_ROLL_MS}ms post=${postRollMs}ms bytes=${blob.size}`,
    )
    return { blob, ext: 'wav', mime: 'audio/wav' }
  }

  return new Promise<RecordedClip>((resolve, reject) => {
    const mr = mediaRecorder
    if (mr === null) {
      reject(new Error('not recording'))
      return
    }
    mr.onstop = () => {
      const mime = chunks[0]?.type || mr.mimeType || 'audio/webm'
      const ext: RecordedClip['ext'] = mime.includes('ogg')
        ? 'ogg'
        : mime.includes('mp4')
          ? 'mp4'
          : mime.includes('m4a') || mime.includes('aac')
            ? 'm4a'
            : 'webm'
      const blob = new Blob(chunks, { type: mime })
      devLog(`stopRecording (one-shot) mime=${mime} ext=${ext} bytes=${blob.size}`)
      cleanupOneShot()
      resolve({ blob, ext, mime })
    }
    try {
      mr.stop()
    } catch (e) {
      cleanupOneShot()
      reject(e instanceof Error ? e : new Error(String(e)))
    }
  })
}

/**
 * Cancel an in-flight capture without producing a clip — for pause / close /
 * unmount. Idempotent and never throws. On the continuous engine the
 * microphone stays open (that's {@link closeMic}'s job); this only drops the
 * marks, so the next tap still gets its pre-roll.
 */
export async function abortRecording(): Promise<void> {
  if (continuous !== null) {
    utterance = null
    resolveTail()
    return
  }
  if (mediaRecorder !== null && mediaRecorder.state !== 'inactive') {
    try {
      // Drop the onstop handler so the discarded clip doesn't resolve a stale
      // stopRecording promise.
      mediaRecorder.onstop = null
      mediaRecorder.stop()
    } catch {
      // Already stopped — harmless.
    }
  }
  cleanupOneShot()
}
